import { CurrencyEnum, centsToUnits } from '@systemeio/ui'
import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  Legend,
  LinearScale,
  ScatterDataPoint,
  Title,
  Tooltip,
} from 'chart.js'
import {
  AffiliateCharInterface,
  AffiliateStatisticCommonInterface,
} from 'modules/affiliate-program/affiliate/types/affiliate-interface'
import {
  getAffiliateChartColor,
  getAffiliateChartTextColor,
} from 'modules/affiliate-program/affiliate/utils/get-affiliate-chart-color'
import { getAffiliateChartOptions } from 'modules/affiliate-program/affiliate/utils/get-affiliate-chart-options'
import { getChartValue } from 'modules/affiliate-program/affiliate/utils/get-chart-value'
import { getSortedChartCurrencies } from 'modules/affiliate-program/affiliate/utils/get-sorted-chart-currencies'
import { hasChartValues } from 'modules/affiliate-program/affiliate/utils/has-chart-values'
import { useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { CurrencyNameEnum } from 'shared/enums/currency-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { currencyFormat } from 'shared/utils/currency-format'
import { toShortDate } from 'shared/utils/date-time-format'

interface AffiliateChartProps {
  chartData?: AffiliateCharInterface
  selectedCurrency?: CurrencyEnum
  affiliateStatistic?: AffiliateStatisticCommonInterface
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const AffiliateChart = ({
  chartData,
  selectedCurrency,
  affiliateStatistic,
}: AffiliateChartProps) => {
  const { user, intlLocale } = useUser()
  const { t } = useLocoTranslation()

  const [data, setData] = useState<ChartData<'bar', (number | ScatterDataPoint | null)[], string>>({
    labels: [],
    datasets: [],
  })

  const [allCurrencies, setAllCurrencies] = useState<CurrencyEnum[]>([])

  useEffect(() => {
    if (affiliateStatistic) {
      setAllCurrencies(Object.keys(affiliateStatistic.total) as CurrencyEnum[])
    }
  }, [affiliateStatistic])

  const [currencies, setCurrencies] = useState<CurrencyEnum[]>([])

  useEffect(() => {
    if (!affiliateStatistic) return

    if (selectedCurrency) {
      setCurrencies([selectedCurrency])
    } else {
      setCurrencies(allCurrencies)
    }
  }, [affiliateStatistic, allCurrencies, selectedCurrency])

  useEffect(() => {
    if (chartData) {
      setData({
        labels: Object.keys(chartData.days).map(el => toShortDate(el, intlLocale)),
        datasets: getSortedChartCurrencies(currencies, chartData.sums).map(cur => ({
          data: Object.keys(chartData.days).map(el => getChartValue(chartData.days[el][cur]) / 100),
          label: t(CurrencyNameEnum[cur]),
          borderRadius: 2,
          backgroundColor: getAffiliateChartColor(cur, allCurrencies),
        })),
      })
    }
  }, [chartData, currencies, allCurrencies, t, intlLocale])

  return (
    <div
      className={'flex flex-col rounded-lg bg-white w-full h-fit justify-between overflow-x-hidden'}
    >
      <div className={'text-darkblue flex gap-1 font-bold px-6 py-4 bg-blue-100 text-[17px]'}>
        <span>{`${t('dashboard.affiliate.commission_earned')}:`}</span>
        {affiliateStatistic && chartData ? (
          <ul className={'flex gap-3'}>
            {getSortedChartCurrencies(currencies, chartData.sums).map(cur => (
              <li key={cur} style={getAffiliateChartTextColor(cur, allCurrencies)}>
                {currencyFormat(
                  centsToUnits(getChartValue(chartData?.sums[cur])),
                  cur,
                  user?.dashboardLocale,
                  true,
                )}
              </li>
            ))}
          </ul>
        ) : (
          <span className={'animate-pulse bg-gray-600 rounded-lg text-transparent px-5'}>.</span>
        )}
      </div>
      <div className={'px-6 py-4'}>
        {chartData ? (
          <Bar
            options={getAffiliateChartOptions(
              hasChartValues(currencies, chartData.sums),
              selectedCurrency,
              user?.dashboardLocale,
            )}
            data={data}
            height={430}
          />
        ) : (
          <div className={'animate-pulse bg-gray-600 h-[430px] rounded-lg text-transparent '}>
            .
          </div>
        )}
      </div>
    </div>
  )
}

export default AffiliateChart
